import { Injectable, inject } from '@angular/core';

import { StreamProvider, StreamType } from '@cds/betting-offer/add-ons';
import { AnimationProviderService } from '@frontend/sports/betting-offer/feature/animation';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { ImgConfigurationData, MediaConfig } from '@frontend/sports/common/client-config-data-access';
import { SPORTS_WITH_MAPPED_STATS } from '@frontend/sports/common/core/data-access/constants';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';

@Injectable({ providedIn: 'root' })
export class EventMediaHelperService {
    private animationProvider = inject(AnimationProviderService);
    private mediaConfig = inject(MediaConfig);
    private imgConfig = inject(ImgConfigurationData);

    hasVideo(event: EventModel): boolean {
        return !!event.stream?.id && (!this.hasPerformV2Provider(event) || event.stream.streamType === StreamType.LiveStream);
    }

    hasLiveVideo(event: EventModel): boolean {
        return (event.live && this.hasVideo(event)) || this.hasPerformV2LiveStream(event);
    }

    hasAnimationOrSimulation(event: EventModel): boolean {
        return this.isRunningBallSupported(event) || this.isAnimationSupported(event);
    }

    hasAnimation(event: EventModel): boolean {
        return this.isAnimationSupported(event);
    }

    hasBetradarAnimation(event: EventModel): boolean {
        return this.hasBetradarAnimationSupported(event);
    }

    hasStats(event: EventModel): boolean {
        const currentEventStats = event.scoreboard.visible && event.scoreboard.started;
        const hasMappedStats =
            SPORTS_WITH_MAPPED_STATS.includes(event.sport.id) && (event.hasFixtureStats || event.hasLeagueStats || currentEventStats);

        return !!event.statsId || !!hasMappedStats;
    }

    hasPerformV2FastData(event: EventModel): boolean {
        return this.hasPerformV2Provider(event) && event.stream?.streamType === StreamType.FastData;
    }

    hasPerformPlayerEnabled(event: EventModel): boolean {
        return this.hasPerformV2Provider(event) && this.mediaConfig.contentPlayerProEnabledSports.includes(event.sport.id);
    }

    private isAnimationSupported(event: EventModel): boolean {
        return (
            this.hasAnimationSupported(event) ||
            this.hasBetradarAnimationSupported(event) ||
            this.hasImgAnimationSupported(event) ||
            this.hasPerformV2FastData(event)
        );
    }

    private isRunningBallSupported(event: EventModel): boolean {
        return !!(event.live && event.runningBallDataId && event.participants?.length);
    }

    private hasAnimationSupported(event: EventModel): boolean {
        return !!(event.live && this.animationProvider.sportAnimations[event.sport.id] && event.participants && event.participants.length);
    }

    private hasBetradarAnimationSupported(event: EventModel): boolean {
        const coverage = this.animationProvider.betRadarSupportedCoverageLevel[event.sport.id];
        return !!(
            event.live &&
            event.betRadarVisualizationId &&
            coverage !== undefined &&
            event.betRadarCoverageLevel &&
            event.betRadarCoverageLevel >= coverage &&
            event.participants &&
            event.participants.length &&
            this.animationProvider.betRadarSupportedAnimations[event.sport.id]
        );
    }

    private hasImgAnimationSupported(event: EventModel): boolean {
        const animationShowTime = DateProviderService.addHours(DateProviderService.getNow(), this.imgConfig.hoursToShowImgAnimation);
        const showPrematchAnimation =
            animationShowTime.getTime() >= event.startDate.getTime() && DateProviderService.getNow().getTime() <= event.startDate.getTime();

        return !!((showPrematchAnimation || event.live) && event.imgEventId);
    }

    private hasPerformV2Provider(event: EventModel): boolean {
        return event.stream?.provider === StreamProvider.PerformV2;
    }

    private hasPerformV2LiveStream(event: EventModel): boolean {
        return this.hasPerformV2Provider(event) && event.stream?.streamType === StreamType.LiveStream;
    }
}
